import {
  IonButton, IonCard, IonIcon, isPlatform,
} from '@ionic/react';
import { downloadOutline } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { APP_VERSION } from '../constants/version';
import './UpdateBanner.css';
import { API_HOST } from '../lib/config';

interface UpdateInfo {
  update_available: boolean;
  force_update: boolean;
  message: string | null;
  latest_version: string;
  store_url: {
    ios: string;
    android: string;
  };
}

const UpdateBanner: React.FC = () => {
  const [updateInfo, setUpdateInfo] = useState<UpdateInfo | null>(null);
  const [dismissed, setDismissed] = useState(false);

  const checkForUpdate = async () => {
    try {
      const platform = isPlatform('ios') ? 'ios' : 'android';
      const currentVersion = APP_VERSION;

      const response = await fetch(
        `${API_HOST}/api/v1/app-version?platform=${platform}&current_version=${currentVersion}`,
      );

      if (response.ok) {
        const data = await response.json();
        if (data.update_available) {
          setUpdateInfo(data);
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to check for updates:', error);
    }
  };

  useEffect(() => {
    checkForUpdate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpdate = () => {
    if (!updateInfo) return;

    const storeUrl = isPlatform('ios')
      ? updateInfo.store_url.ios
      : updateInfo.store_url.android;

    window.open(storeUrl, '_system');
  };

  if (!updateInfo || dismissed) {
    return null;
  }

  if (updateInfo.force_update) {
    // Force update - block entire app
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.9)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
      >
        <IonCard style={{
          maxWidth: '400px', width: '100%', padding: '20px', textAlign: 'center',
        }}
        >
          <h2>Update Required</h2>
          <p>{updateInfo.message || 'Please update to the latest version to continue.'}</p>
          <p style={{ fontSize: '0.9em', color: '#666' }}>
            Latest version: {updateInfo.latest_version}
          </p>
          <IonButton expand="block" onClick={handleUpdate}>
            <IonIcon icon={downloadOutline} slot="start" />
            Update Now
          </IonButton>
        </IonCard>
      </div>
    );
  }

  // Soft update - dismissible banner
  return (
    <div className="update-banner">
      <IonCard className="update-banner-card">
        <div className="update-banner-content">
          <div className="update-banner-icon">
            <IonIcon icon={downloadOutline} />
          </div>
          <div className="update-banner-text">
            <strong>Update Available</strong>
            <p>{updateInfo.message || 'A new version is available.'}</p>
          </div>
          <div className="update-banner-actions">
            <IonButton size="small" onClick={handleUpdate}>
              Update
            </IonButton>
            <IonButton size="small" onClick={() => setDismissed(true)}>
              Dismiss
            </IonButton>
          </div>
        </div>
      </IonCard>
    </div>
  );
};

export default UpdateBanner;
